/**
 * useConfirmDialog Composable
 *
 * Provides a programmatic way to show confirmation dialogs.
 * Replaces vuetify-confirm plugin with a reactive Vue 3 composable.
 *
 * Features:
 * - Promise-based API for async/await usage
 * - Customizable title, content, and buttons
 * - TypeScript support
 * - Integrates with existing DialogConfirm.vue component
 *
 * @version 2.0.0-alpha.10
 */

import { reactive, readonly } from 'vue'

// =============================================================================
// Types
// =============================================================================

export interface ConfirmDialogOptions {
  /** Dialog title */
  title?: string
  /** Dialog content/message */
  content: string
  /** Confirm button text */
  confirmText?: string
  /** Cancel button text */
  cancelText?: string
  /** Confirm button color */
  confirmColor?: string
  /** Dialog width */
  width?: string | number
  /** Whether dialog is persistent (can't close by clicking outside) */
  persistent?: boolean
  /**
   * Disable auto-fullscreen on mobile.
   *
   * By default (`false`), the dialog goes fullscreen on mobile/tablet —
   * coherent with `GgDialog`'s default and ideal for content-rich confirmations.
   *
   * Set to `true` for short, compact confirmation messages where a fullscreen
   * sheet would feel disorienting (e.g. "Prodotto già inserito"). The dialog
   * will then respect `width` on mobile too.
   *
   * @default false
   */
  noAutoFullscreen?: boolean
}

export interface ConfirmDialogState {
  /** Whether dialog is visible */
  isOpen: boolean
  /** Dialog title */
  title: string
  /** Dialog content/message */
  content: string
  /** Confirm button text */
  confirmText: string
  /** Cancel button text */
  cancelText: string
  /** Confirm button color */
  confirmColor: string
  /** Dialog width */
  width: string | number
  /** Whether dialog is persistent */
  persistent: boolean
  /** Disable auto-fullscreen on mobile (forwarded to GgDialog) */
  noAutoFullscreen: boolean
}

export interface UseConfirmDialogReturn {
  /** Current dialog state */
  state: Readonly<ConfirmDialogState>
  /** Show confirmation dialog and return promise */
  confirm: (options: ConfirmDialogOptions | string) => Promise<boolean>
  /** Handle confirm action (called by dialog component) */
  handleConfirm: () => void
  /** Handle cancel action (called by dialog component) */
  handleCancel: () => void
  /** Close dialog programmatically */
  close: () => void
}

// =============================================================================
// Module State (singleton pattern)
// =============================================================================

const state = reactive<ConfirmDialogState>({
  isOpen: false,
  title: 'Conferma',
  content: '',
  confirmText: 'Conferma',
  cancelText: 'Annulla',
  confirmColor: 'primary',
  width: 450,
  persistent: true,
  noAutoFullscreen: true,
})

let resolvePromise: ((value: boolean) => void) | null = null

// =============================================================================
// Actions
// =============================================================================

function resetState(): void {
  state.isOpen = false
  state.title = 'Conferma'
  state.content = ''
  state.confirmText = 'Conferma'
  state.cancelText = 'Annulla'
  state.confirmColor = 'primary'
  state.width = 450
  state.persistent = true
  state.noAutoFullscreen = true
  resolvePromise = null
}

function confirm(options: ConfirmDialogOptions | string): Promise<boolean> {
  return new Promise((resolve) => {
    // Store resolve function
    resolvePromise = resolve

    // Handle string shorthand
    if (typeof options === 'string') {
      state.content = options
    } else {
      // Apply options
      state.title = options.title ?? 'Conferma'
      state.content = options.content
      state.confirmText = options.confirmText ?? 'Conferma'
      state.cancelText = options.cancelText ?? 'Annulla'
      state.confirmColor = options.confirmColor ?? 'primary'
      state.width = options.width ?? 450
      state.persistent = options.persistent ?? true
      state.noAutoFullscreen = options.noAutoFullscreen ?? true
    }

    // Show dialog
    state.isOpen = true
  })
}

function handleConfirm(): void {
  if (resolvePromise) {
    resolvePromise(true)
  }
  resetState()
}

function handleCancel(): void {
  if (resolvePromise) {
    resolvePromise(false)
  }
  resetState()
}

function close(): void {
  handleCancel()
}

// =============================================================================
// Composable
// =============================================================================

/**
 * Composable for programmatic confirmation dialogs
 *
 * @example
 * ```ts
 * // In a component
 * const { confirm } = useConfirmDialog()
 *
 * // Simple usage
 * const confirmed = await confirm('Sei sicuro di voler eliminare?')
 * if (confirmed) {
 *   // Proceed with deletion
 * }
 *
 * // With options
 * const confirmed = await confirm({
 *   title: 'Elimina elemento',
 *   content: 'Questa azione è irreversibile. Continuare?',
 *   confirmText: 'Elimina',
 *   cancelText: 'Annulla',
 *   confirmColor: 'error'
 * })
 * ```
 *
 * @example
 * ```vue
 * <!-- In App.vue or a layout component -->
 * <template>
 *   <ConfirmDialogProvider />
 *   <!-- rest of app -->
 * </template>
 *
 * <script setup>
 * import ConfirmDialogProvider from '@/components/ConfirmDialogProvider.vue'
 * </script>
 * ```
 */
export function useConfirmDialog(): UseConfirmDialogReturn {
  return {
    state: readonly(state) as Readonly<ConfirmDialogState>,
    confirm,
    handleConfirm,
    handleCancel,
    close
  }
}

// =============================================================================
// Preset Dialogs
// =============================================================================

/**
 * Show a delete confirmation dialog
 *
 * @example
 * ```ts
 * import { confirmDelete } from '@/composables/useConfirmDialog'
 *
 * async function deleteItem() {
 *   if (await confirmDelete('questo elemento')) {
 *     await api.delete(`/items/${id}`)
 *   }
 * }
 * ```
 */
export function confirmDelete(itemName?: string): Promise<boolean> {
  return confirm({
    title: 'Conferma eliminazione',
    content: itemName
      ? `Sei sicuro di voler eliminare ${itemName}? Questa azione è irreversibile.`
      : 'Sei sicuro di voler eliminare questo elemento? Questa azione è irreversibile.',
    confirmText: 'Elimina',
    cancelText: 'Annulla',
    confirmColor: 'error'
  })
}

/**
 * Show a discard changes confirmation dialog
 *
 * @example
 * ```ts
 * import { confirmDiscard } from '@/composables/useConfirmDialog'
 *
 * async function navigateAway() {
 *   if (hasUnsavedChanges && !(await confirmDiscard())) {
 *     return // Don't navigate
 *   }
 *   router.push('/home')
 * }
 * ```
 */
export function confirmDiscard(): Promise<boolean> {
  return confirm({
    title: 'Modifiche non salvate',
    content: 'Ci sono modifiche non salvate. Sei sicuro di voler uscire?',
    confirmText: 'Esci senza salvare',
    cancelText: 'Torna indietro',
    confirmColor: 'warning'
  })
}

/**
 * Show a logout confirmation dialog
 *
 * @example
 * ```ts
 * import { confirmLogout } from '@/composables/useConfirmDialog'
 *
 * async function logout() {
 *   if (await confirmLogout()) {
 *     await authStore.logout()
 *   }
 * }
 * ```
 */
export function confirmLogout(): Promise<boolean> {
  return confirm({
    title: 'Conferma logout',
    content: 'Sei sicuro di voler uscire?',
    confirmText: 'Esci',
    cancelText: 'Annulla',
    confirmColor: 'primary'
  })
}

export default useConfirmDialog
